import { TIERS_TYPE_CLIENT, TIERS_SOUS_TYPE_AUCUN } from '@/configuration/tiers/tiersConf';
import { CATEGORIE_DIVERS, CATEGORIE_LOCATION, CATEGORIE_UNITAIRE } from '@/configuration/catalogue/produitConf';
import { NO_PAGINATION } from '@/configuration/clientTableConf';
import { getAll as getAllTiers } from '@/api/tiers/index';
import { getAll as getAllProduits } from '@/api/produit/index';
import { create as createFacture } from '@/api/facture/index';
import { columnsFacture, columnsLigneFacture } from '@/services/import/facture/templateCommande';
import gestionParametre from '@/services/import/General/gestionParametre';
import i18n from '@/i18n/index';
import store from '@/store/store';
import { genererFacture } from './genererFacture';

function ErrorLigneCommandeTranslate(error) {
  const numLigneCommande = error.split('_')[2];
  const errorLigne = error.substr(`LIGNE_COMMANDE_${numLigneCommande}_`.length, error.length);
  // eslint-disable-next-line no-use-before-define
  const traduction = ErrorCommandeTranslate([errorLigne]);
  return i18n.t('importationCommande.errors.lignesError', { num: numLigneCommande, error: traduction });
}

export function ErrorCommandeTranslate(errors) {
  if (!errors || !Array.isArray(errors)) return [];
  return errors.map(error => {
    if (error.startsWith('LIGNE_COMMANDE_')) {
      return ErrorLigneCommandeTranslate(error);
    }
    if (error.endsWith('_CLIENT_NOT_FOUND')) {
      return i18n.t('importationCommande.errors.clientNotFound', { name: error.split('_')[0] });
    }
    if (error.endsWith('_NO_MATCH')) {
      return i18n.t('importationCommande.errors.notFoundPropertie', { label: error.split('_')[0] });
    }
    if (error.endsWith('_IS_REQUIRED')) {
      return i18n.t('importationCommande.errors.requiredPropertie', { label: error.split('_')[0] });
    }
    if (error.includes('_MAX_LENGTH_')) {
      return i18n.t('importationCommande.errors.maxLengthPropertie', { label: error.split('_')[0], length: error.split('_')[error.split('_').length - 1] });
    }
    switch (error) {
      case 'COMMANDE_HAS_NO_LIGNES':
        return i18n.t('importationCommande.errors.hasNoLignes');
      case 'MONTANT_ERROR':
        return i18n.t('importationCommande.errors.montantError');
      case 'ECHEANCIER_PAIEMENT':
        return i18n.t('importationCommande.errors.echeancierPaiement');
      default:
    }
    return 'Problème Survenu';
  }).join(' - ');
}

function isNoPropertie(value) {
  return value === '' || value === undefined || value === null;
}

function validationLigne(ligne, lignesCommandes) {
  let errors = [];
  columnsFacture.forEach(column => {
    const columnErrors = column.checkValue(ligne[column.key]);
    if (columnErrors.length > 0) errors = errors.concat(columnErrors);
  });
  if (lignesCommandes.length === 0) errors.push('COMMANDE_HAS_NO_LIGNES');
  for (let i = 0; i < lignesCommandes.length; i += 1) {
    const ligneCommande = lignesCommandes[i];
    let errorLigne = [];
    columnsLigneFacture.forEach(column => {
      const columnErrors = column.checkValue(ligneCommande[column.key]);
      if (columnErrors.length > 0) errorLigne = errorLigne.concat(columnErrors.map(e => `LIGNE_COMMANDE_${ligneCommande.numLigne}_${e}`));
    });
    if (ligneCommande.codeProduit) {
      if (isNoPropertie(ligneCommande.dateDebut)) errorLigne.push(`LIGNE_COMMANDE_${ligneCommande.numLigne}_${columnsLigneFacture.find(c => c.key === 'dateDebut')?.label || 'X'}_IS_REQUIRED`);
      if (isNoPropertie(ligneCommande.dateFin)) errorLigne.push(`LIGNE_COMMANDE_${ligneCommande.numLigne}_${columnsLigneFacture.find(c => c.key === 'dateFin')?.label || 'X'}_IS_REQUIRED`);
      if (isNoPropertie(ligneCommande.quantiteDispo)) errorLigne.push(`LIGNE_COMMANDE_${ligneCommande.numLigne}_${columnsLigneFacture.find(c => c.key === 'quantiteDispo')?.label || 'X'}_IS_REQUIRED`);
    }
    if (errorLigne.length > 0) errors = errors.concat(errorLigne);
  }
  if (errors.length > 0) throw errors;
}

export async function makeCommandeGenerated(file) {
  const fileCommandes = file[0];
  const lignesCommandes = file[1];
  const commandes = [];
  const errors = [];
  const calls = await Promise.all([
    getAllTiers({
      query: {
        societeId: store.getters['societe/societeActiveId'],
        type: TIERS_TYPE_CLIENT,
        sousType: [TIERS_SOUS_TYPE_AUCUN],
      },
      limit: NO_PAGINATION,
    }),
    getAllProduits({
      query:
        {
          actif: true,
          categorie: [CATEGORIE_DIVERS, CATEGORIE_LOCATION, CATEGORIE_UNITAIRE],
        },
      limit: NO_PAGINATION,
    }),
    gestionParametre('commun/ModeReglement'),
    gestionParametre('commun/DelaiReglement'),
  ]);
  const utils = {
    tiers: calls[0].data.result.items,
    produits: calls[1].data.result.items,
    modes: calls[2],
    delais: calls[3],
  };
  // on retire la premiere ligne
  for (let i = 1; i < fileCommandes.length; i += 1) {
    try {
      validationLigne(
        fileCommandes[i], lignesCommandes.filter(l => l.codeCommande === fileCommandes[i].code),
      );
      // eslint-disable-next-line no-await-in-loop
      const newCommande = await genererFacture(
        fileCommandes[i],
        lignesCommandes.filter(l => l.codeCommande === fileCommandes[i].code),
        utils,
      );
      commandes.push(newCommande);
    } catch (e) {
      errors.push({ numLigne: fileCommandes[i].numLigne, errors: e });
    }
  }
  if (errors.length > 0) {
    errors.unshift({ total: fileCommandes.length - 1, totalErrors: errors.length });
    throw errors;
  }
  return commandes;
}

export async function makeCommandeImport(commandes) {
  const errors = [];
  for (let i = 0; i < commandes.length; i += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      await createFacture(commandes[i]);
    } catch (e) {
      errors.push({ commande: commandes[i], error: e });
    }
  }
  if (errors.length > 0) throw errors;
}
